import { type Call as TelnyxCall } from '@telnyx/webrtc';
import type {
  InitializeArgs,
  CallOptionsArgs,
  CallState,
  WebRTCEventName,
  WebRTCEventHandler,
  TelnyxNotification,
} from './types';
import { WebRTCCallEventBus } from './WebRTCCallEventBus';
import { LocalMicController } from './LocalMicController';
import { RemoteAudioChannel } from './RemoteAudioChannel';
import { TelnyxRTCFactory } from './TelnyxRTCFactory';
import { CallInfoParser } from './CallInfoParser';
import { CallStateMonitor } from './CallStateMonitor';
import { CallLifecycleHandler } from './CallLifecycleHandler';

const CONNECTION_TIMEOUT = 10000;

export class WebRTCCallService {
  public isConnected = false;
  private client: ReturnType<TelnyxRTCFactory['create']> | null = null;
  private currentCall: TelnyxCall | null = null;
  private currentCallMetadata: { contactId?: number; userId?: number } = {};
  private readonly eventManager: WebRTCCallEventBus;
  private readonly mic: LocalMicController;
  private readonly remoteAudio: RemoteAudioChannel;
  private readonly rtcFactory: TelnyxRTCFactory;
  private debug = false;
  private readonly stateMonitor: CallStateMonitor;
  private readonly lifecycle: CallLifecycleHandler;
  private customAudioConstraints: MediaStreamConstraints | null = null;

  constructor({
    eventManager = new WebRTCCallEventBus(),
    mic = new LocalMicController(),
    remoteAudio = new RemoteAudioChannel(),
    rtcFactory = new TelnyxRTCFactory(),
  }: {
    eventManager?: WebRTCCallEventBus;
    mic?: LocalMicController;
    remoteAudio?: RemoteAudioChannel;
    rtcFactory?: TelnyxRTCFactory;
  } = {}) {
    this.eventManager = eventManager;
    this.mic = mic;
    this.remoteAudio = remoteAudio;
    this.rtcFactory = rtcFactory;

    this.stateMonitor = new CallStateMonitor(this.handleCallStateChange.bind(this));
    this.lifecycle = new CallLifecycleHandler(
      this.remoteAudio,
      this.eventManager,
      this.debugLog.bind(this),
      this.cleanup.bind(this)
    );
  }

  public async initialize({ credentials, debug = false }: InitializeArgs): Promise<void> {
    if (this.client) return;

    this.debug = debug;

    this.client = this.rtcFactory.create(credentials, this.debug);

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Connection timeout'));
      }, CONNECTION_TIMEOUT);

      const onReady = () => {
        this.debugLog('Telnyx client ready');
        console.log('🔧 WebRTC Service: Client ready, connected to Telnyx');
        this.isConnected = true;
        this.eventManager.emit('ready');
        cleanup();
        resolve();
      };

      const onError = (error: Error) => {
        this.debugLog('Telnyx error:', error);
        console.error('🔧 WebRTC Service: Connection error:', error);
        this.eventManager.emit('error', error);
        cleanup();
        reject(error);
      };

      const onNotification = (notification: TelnyxNotification) => {
        this.handleNotification(notification);
      };

      const onSocketClose = () => {
        this.debugLog('Telnyx socket closed');
        this.isConnected = false;
      };

      const cleanup = () => {
        clearTimeout(timeout);
        this.client?.off('telnyx.ready', onReady);
        this.client?.off('telnyx.error', onError);
        this.client?.off('telnyx.notification', onNotification);
        this.client?.off('telnyx.socket.close', onSocketClose);
      };

      this.client!.on('telnyx.ready', onReady)
        .on('telnyx.error', onError)
        .on('telnyx.notification', onNotification)
        .on('telnyx.socket.close', onSocketClose);

      this.client!.connect().catch(error => {
        cleanup();
        reject(error);
      });
    });
  }

  public async makeCall({
    destinationNumber,
    callerNumber,
    contactId,
    userId,
  }: Omit<CallOptionsArgs, 'localStream'>): Promise<void> {
    this.debugLog('makeCall method invoked');
    if (!this.client || !this.isConnected) throw new Error('WebRTC client not connected');

    try {
      let localStream: MediaStream;
      try {
        localStream = this.mic.getLocalStream();
      } catch {
        this.debugLog('Requesting microphone permission...');
        // Use custom audio constraints if set, otherwise use default
        localStream = await this.mic.requestPermission(this.customAudioConstraints || undefined);
      }

      const formattedDestination = CallInfoParser.formatE164(destinationNumber || '');
      const formattedCaller = CallInfoParser.formatE164(callerNumber || '');

      if (this.currentCall) {
        this.debugLog('Cleaning up existing call');
        this.currentCall.hangup();
        this.currentCall = null;
      }

      this.currentCallMetadata = { contactId, userId };

      const call = this.client.newCall({
        destinationNumber: formattedDestination,
        callerNumber: formattedCaller,
        audio: true,
        video: false,
        localStream,
        custom_headers: [
          { name: 'X-Contact-ID', value: contactId?.toString() || '' },
          { name: 'X-User-ID', value: userId?.toString() || '' },
        ],
      });

      this.debugLog('Call object received from SDK:', call);
      this.debugLog('Call prototype methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(call)));
      this.debugLog('Call metadata:', this.currentCallMetadata);
      this.debugLog('Call configuration:', {
        destinationNumber: formattedDestination,
        callerNumber: formattedCaller,
        custom_headers: [
          { name: 'X-Contact-ID', value: contactId?.toString() || '' },
          { name: 'X-User-ID', value: userId?.toString() || '' },
        ],
      });

      this.currentCall = call;
      this.stateMonitor.start(call);
    } catch (err) {
      this.debugLog('Error creating call:', err);
      throw err;
    }
  }

  public hangUp(): void {
    if (!this.currentCall) return;
    this.currentCall.hangup();
  }

  public toggleMute(): boolean {
    return this.mic.toggleMute();
  }

  public get isMuted(): boolean {
    return this.mic.getMuteState();
  }

  public on(event: WebRTCEventName, handler: WebRTCEventHandler): void {
    this.eventManager.on(event, handler);
  }

  public async requestMicrophonePermission(overrides?: MediaStreamConstraints): Promise<MediaStream> {
    return this.mic.requestPermission(overrides);
  }
  
  public async setAudioConstraints(constraints: MediaStreamConstraints): Promise<void> {
    this.customAudioConstraints = constraints;
    this.debugLog('Audio constraints set:', constraints);
  }
  
  public async setSpeakerId(sinkId: string): Promise<void> {
    if (sinkId !== 'default') {
      await this.remoteAudio.setSinkId(sinkId);
      this.debugLog('Speaker device set:', sinkId);
    }
  }

  private async cleanup(): Promise<void> {
    this.stateMonitor.stop();
    this.mic.cleanup();
    this.remoteAudio.cleanup();
    this.currentCall = null;
  }

  private handleCallStateChange(state: CallState) {
    const c = this.currentCall;
    if (!c) return;

    const callInfo = CallInfoParser.extract(c);
    this.debugLog(`Call state change: ${state}`, callInfo);
    this.eventManager.emit('callStateChanged', { state, call: callInfo });

    switch (state) {
      case 'active':
      case 'answered':
        this.lifecycle.handleActive(c, callInfo);
        break;
      case 'hangup':
      case 'destroy':
        this.lifecycle.handleEnd(state, callInfo);
        break;
    }
  }

  private handleNotification(notification: TelnyxNotification): void {
    this.debugLog('Telnyx notification:', notification);

    if (notification.type === 'callUpdate' && notification.call) {
      const { state, remoteStream } = notification.call;

      this.attachRemoteStream(remoteStream);
      if (state && CallInfoParser.isValidState(state)) {
        this.stateMonitor.externalStateUpdate(state);
      }
    }
  }

  private debugLog(...args: unknown[]): void {
    if (this.debug) console.log('[WebRTCCallService]', ...args);
  }

  private attachRemoteStream(stream?: MediaStream): void {
    if (stream && CallInfoParser.isMediaStream(stream)) {
      this.remoteAudio.attachStream(stream);
    }
  }
}

export const webRTCCallSingletonService = new WebRTCCallService();
